import React from 'react'
import { Tooltip } from '@mui/material'
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import { Button } from 'src/components/shared/Button'
import { retryActionJobStatusTitles } from 'src/constants/scheduler/constants'
import type { RetryActionJobStatus } from 'src/constants/scheduler/enums'
import type { RetryActionJob } from 'src/interfaces/retryConfig'
import { formatDateTime, parseFlexibleDateTime, parseUnixTimestamp } from 'src/utils/grid'
import { canRescheduleJob } from './canRescheduleJob'

/**
 * Note the two different time representations in one table: `createdAt` is unix SECONDS
 * (backend `BaseEntity`) while `scheduledAt`/`processedAt` are timestamp STRINGS. Reading either
 * with the other's parser renders a date in 1970. Paired with `formatDateTime` the way the repo
 * already does it elsewhere (see `webhookDeliveriesGrid.tsx`), rather than an inline Luxon call.
 *
 * The endpoint this table reads from has no sort, so every column is `sortable: false`. The
 * table itself has no pagination either — see `RetriesHistoryTable`'s own comment.
 *
 * A factory rather than a module constant (same shape as `buildWebhookDeliveryColumns`): the
 * actions column closes over the page's reschedule handler. Callers must memoize the result.
 *
 * `onReschedule` is `null` when the user lacks `schedulers_manage`; the actions column holds only
 * the Reschedule button, so it is dropped rather than left empty (ENG-1207 part 2).
 */
export const buildRetriesHistoryColumns = (
  onReschedule: ((job: RetryActionJob) => void) | null,
): Array<GridColDef<RetryActionJob>> => {
  const columns: Array<GridColDef<RetryActionJob>> = [
    {
      field: 'createdAt',
      headerName: 'Created at',
      flex: 1,
      sortable: false,
      valueGetter: parseUnixTimestamp,
      valueFormatter: formatDateTime,
    },
    {
      field: 'scheduledAt',
      headerName: 'Scheduled at',
      flex: 1,
      sortable: false,
      valueGetter: parseFlexibleDateTime,
      valueFormatter: formatDateTime,
    },
    {
      field: 'processedAt',
      headerName: 'Processed at',
      flex: 1,
      sortable: false,
      valueGetter: parseFlexibleDateTime,
      valueFormatter: formatDateTime,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      sortable: false,
      valueGetter: (value: RetryActionJobStatus) => {
        return retryActionJobStatusTitles[value] ?? value
      },
    },
  ]

  if (onReschedule === null) {
    return columns
  }

  // Bound after the null check so the cell closure below sees a non-null handler.
  const handleReschedule = onReschedule

  return [
    ...columns,
    {
      field: 'actions',
      headerName: '',
      width: 140,
      sortable: false,
      // Pinned right and not reorderable: it is the row's action, not data, so it stays put when
      // the other columns scroll horizontally.
      align: 'right',
      headerAlign: 'right',
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams<RetryActionJob>) => {
        const isReschedulable = canRescheduleJob(params.row)

        /*
         * The label stays "Reschedule" in both states and the REASON moves to a tooltip.
         * `canRescheduleJob` gates on `status === Scheduled`, so a disabled button also covers a
         * PROCESSING job — "Can't reschedule completed job" would be a false statement there.
         * Putting the full explanation in the label instead would make it a sentence inside a
         * table cell, which is what overflowed the column before.
         */
        const button = (
          <Button
            size="small"
            color="grey"
            disabled={!isReschedulable}
            onClick={() => {
              return handleReschedule(params.row)
            }}
            data-testid={`retries-history-reschedule-${params.row.id}`}
          >
            Reschedule
          </Button>
        )

        if (isReschedulable) {
          return button
        }

        // A disabled MUI button fires no pointer events, so the tooltip needs a wrapper to
        // listen on — otherwise it never opens and the reason is invisible.
        return (
          <Tooltip title="Only scheduled jobs can be rescheduled">
            <span>{button}</span>
          </Tooltip>
        )
      },
    },
  ]
}
